// src/pages/Customer/Dashboard.js (INTEGRATING FIREBASE PERSISTENCE FOR DAILY STATS)
import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebase';
import {
    collection,
    query,
    orderBy,
    limit,
    getDocs,
    doc,
    getDoc,
    setDoc,
} from 'firebase/firestore';
import { FaHeartbeat, FaWeight, FaBed, FaTint, FaCommentDots, FaCheckCircle, FaRunning, FaCalendarAlt, FaBell} from 'react-icons/fa';
import '../../styles/CustomerDashboard.css';
import RealProgressGraph from '../../components/RealProgressGraph';
import WeeklyReportSummary from '../../components/WeeklyReportSummary';
import FocusFlowGame from '../../components/FocusFlowGame';
import AppNotifications from '../../components/AppNotifications';
// Default initial stats for a user with no data yet
const DEFAULT_DAILY_STATS = {
    exerciseTime: 0, 
    currentWeight: 0, 
    initialWeight: 90.5, 
    sleepTime: 0,
    sleepGoal: 7.5,
    waterIntake: 0,
    waterGoal: 3.0,
};

// --- Utility Functions (These remain the same) ---
const getFormattedDate = (date) => date.toISOString().split('T')[0];

const getLastSevenDays = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        dates.push(getFormattedDate(d));
    }
    return dates;
};
// --- END Utility Functions ---


// 1. Motivational Quote (Remains the same)
const MotivationalQuote = ({ currentUser, profileName }) => {
    const quotes = [
        "The journey of a thousand miles begins with a single step.",
        "Your body can stand almost anything. It's your mind that you have to convince.",
        "Strive for progress, not perfection.",
        "The best project you'll ever work on is you.",
    ];
    const quote = quotes[Math.floor(Math.random() * quotes.length)];
    const greetingName = profileName || currentUser?.email?.split('@')[0] || 'Customer';

    return (
        <div className="card greeting-card">
            <div className="greeting">👋 Hello, {greetingName}!</div>
            <p className="quote">"{quote}"</p>
        </div>
    );
};

// 2. Quick Stats DISPLAY Component (Remains the same)
const QuickStatsDisplay = ({ exerciseTime, currentWeight, initialWeight, sleepTime, sleepGoal, waterIntake, waterGoal }) => {

    // ⭐ CRITICAL FIX: Ensure all inputs are treated as numbers, defaulting to 0 if null/undefined
    const safeExerciseTime = Number(exerciseTime) || 0;
    const safeCurrentWeight = Number(currentWeight) || 0;
    const safeInitialWeight = Number(initialWeight) || DEFAULT_DAILY_STATS.initialWeight; 
    const safeSleepTime = Number(sleepTime) || 0;
    const safeSleepGoal = Number(sleepGoal) || DEFAULT_DAILY_STATS.sleepGoal;
    const safeWaterIntake = Number(waterIntake) || 0;
    const safeWaterGoal = Number(waterGoal) || DEFAULT_DAILY_STATS.waterGoal;
    
    // Check if stats are placeholders (0) to avoid weird UI/calculations
    const weightDisplay = safeCurrentWeight > 0 ? safeCurrentWeight.toFixed(1) : '--';
    const progressWidth = safeCurrentWeight > 0 && safeInitialWeight > 0 ? Math.max(0, 100 - ((safeCurrentWeight / safeInitialWeight) * 100 - 100)) : 0;
    const progressColor = safeCurrentWeight > 0 && safeCurrentWeight <= safeInitialWeight ? '#4caf50' : '#f44336';


    return (
        <div className="stats-grid">
            <div className="card stat-card exercise">
                <FaHeartbeat className="icon" />
                <div className="stat-value">{safeExerciseTime.toFixed(0)} mins</div>
                <div className="stat-label">Exercise Today</div>
            </div>
            <div className="card stat-card weight">
                <FaWeight className="icon" />
                <div className="stat-value">{weightDisplay} kgs</div>
                <div className="stat-label">Current Weight</div>
                <div className="progress-bar-container">
                    <div className="progress-bar" style={{
                        width: `${progressWidth}%`,
                        backgroundColor: progressColor
                    }}></div>
                </div>
            </div>
            <div className="card stat-card sleep">
                <FaBed className="icon" />
                <div className="stat-value">{safeSleepTime.toFixed(1)} hrs</div>
                <div className="stat-label">Sleep Last Night</div>
                <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${Math.min(100, (safeSleepTime / safeSleepGoal) * 100)}%` }}></div>
                </div>
            </div>
            <div className="card stat-card water">
                <FaTint className="icon" />
                <div className="stat-value">{safeWaterIntake.toFixed(1)}L / {safeWaterGoal.toFixed(1)}L</div>
                <div className="stat-label">Water Intake</div>
                <div className="progress-bar-container"><div className="progress-bar" style={{ width: `${(safeWaterIntake / safeWaterGoal) * 100}%` }}></div></div>
            </div>
        </div>
    );
};


// 3. Daily Stats Input Logger (Remains the same)
const DailyStatsLogger = ({ setStats, currentStats, uid, selectedDate, today }) => {
    // Only pre-fill input fields if the value is non-zero, making it easier to log
    const [exerciseInput, setExerciseInput] = useState(currentStats.exerciseTime > 0 ? currentStats.exerciseTime.toString() : '');
    const [weightInput, setWeightInput] = useState(currentStats.currentWeight > 0 ? currentStats.currentWeight.toString() : '');
    const [sleepInput, setSleepInput] = useState(currentStats.sleepTime > 0 ? currentStats.sleepTime.toString() : '');
    const [waterInput, setWaterInput] = useState(currentStats.waterIntake > 0 ? currentStats.waterIntake.toString() : '');

    const [submissionMessage, setSubmissionMessage] = useState({ message: '', type: '' });

    // ⭐ Sync input fields with currentStats when it changes (i.e., when date changes)
    useEffect(() => {
        setExerciseInput(currentStats.exerciseTime > 0 ? currentStats.exerciseTime.toString() : '');
        setWeightInput(currentStats.currentWeight > 0 ? currentStats.currentWeight.toString() : '');
        setSleepInput(currentStats.sleepTime > 0 ? currentStats.sleepTime.toString() : '');
        setWaterInput(currentStats.waterIntake > 0 ? currentStats.waterIntake.toString() : '');
    }, [currentStats]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!uid) {
            setSubmissionMessage({ message: '❌ Error: User not authenticated.', type: 'error' });
            return;
        }

        setSubmissionMessage({ message: 'Saving...', type: '' });

        const newStats = {};
        let updateCount = 0;

        // Validation and aggregation
        if (exerciseInput && !isNaN(parseFloat(exerciseInput))) { newStats.exerciseTime = parseFloat(exerciseInput); updateCount++; }
        if (weightInput && !isNaN(parseFloat(weightInput))) { newStats.currentWeight = parseFloat(weightInput); updateCount++; }
        if (sleepInput && !isNaN(parseFloat(sleepInput))) { newStats.sleepTime = parseFloat(sleepInput); updateCount++; }
        if (waterInput && !isNaN(parseFloat(waterInput))) { newStats.waterIntake = parseFloat(waterInput); updateCount++; }

        if (updateCount > 0) {
            // New logic: Save daily stats to a document under dailyProgress
            const progressDocRef = doc(db, 'progress', uid, 'dailyProgress', selectedDate);
            const progressData = {
                exerciseTime: newStats.exerciseTime !== undefined ? newStats.exerciseTime : currentStats.exerciseTime,
                currentWeight: newStats.currentWeight !== undefined ? newStats.currentWeight : currentStats.currentWeight,
                sleepTime: newStats.sleepTime !== undefined ? newStats.sleepTime : currentStats.sleepTime,
                waterIntake: newStats.waterIntake !== undefined ? newStats.waterIntake : currentStats.waterIntake,
                sleepGoal: currentStats.sleepGoal, // Keep goals consistent
                waterGoal: currentStats.waterGoal,
                lastUpdated: new Date().toISOString()
            };
            
            try {
                // Save to dailyProgress document for the selected date
                await setDoc(progressDocRef, progressData, { merge: true });
                
                // If logging for TODAY, also update the main quick stats display in the Dashboard parent component
                if (selectedDate === today) {
                    setStats(progressData);
                }
                
                // Update the inputs to reflect the newly logged data (especially important when logging an old date)
                setStats({...currentStats, ...progressData});

                setSubmissionMessage({ message: `✅ Stats for ${selectedDate} logged successfully!`, type: 'success' });
            } catch (error) {
                console.error("Error saving stats to Firebase:", error);
                setSubmissionMessage({ message: `❌ Failed to save stats: ${error.message}`, type: 'error' });
            }

            setTimeout(() => setSubmissionMessage({ message: '', type: '' }), 5000);

        } else {
            setSubmissionMessage({ message: '❌ Please enter at least one valid number to log.', type: 'error' });
        }
    };
    
    // Determine the title based on the selected date
    const dateTitle = selectedDate === today ? 'Today' : new Date(selectedDate).toLocaleDateString();

    return (
        <div className="card full-width daily-logger-card">
            <h3><FaRunning /> Log Stats for {dateTitle}</h3>

            {submissionMessage.message && (
                <div className={`submission-message ${submissionMessage.type}`}>
                    {submissionMessage.message}
                </div>
            )}

            <form onSubmit={handleSubmit} className="stats-logger-form">
                <div className="form-row">
                    <label>
                        Exercise (mins):
                        <input type="number" step="5" value={exerciseInput} onChange={(e) => setExerciseInput(e.target.value)} placeholder="e.g., 45" />
                    </label>
                    <label>
                        Weight (kgs):
                        <input type="number" step="0.1" value={weightInput} onChange={(e) => setWeightInput(e.target.value)} placeholder="e.g., 90.5" />
                    </label>
                </div>
                <div className="form-row">
                    <label>
                        Sleep (hrs):
                        <input type="number" step="0.1" value={sleepInput} onChange={(e) => setSleepInput(e.target.value)} placeholder="e.g., 7.5" />
                    </label>
                    <label>
                        Water (L):
                        <input type="number" step="0.1" value={waterInput} onChange={(e) => setWaterInput(e.target.value)} placeholder="e.g., 2.5" />
                    </label>
                </div>
                <button type="submit">Submit Log for {dateTitle}</button>
            </form>
        </div>
    );
};


// 4. Diet Plan Card (Remains the same)
const DietPlanSection = ({ dietPlanData, isMobile, mealTimings }) => {
    // ... (logic remains the same)
    const todayNormalized = getFormattedDate(new Date());

    const getDayPlan = () => {
        return dietPlanData.weeklyDietPlan.find(p => {
            const planDateNormalized = p.date ? getFormattedDate(new Date(p.date)) : null;
            return planDateNormalized === todayNormalized;
        }) || dietPlanData.weeklyDietPlan[0];
    };

    const todayPlan = getDayPlan();

    return (
        <div className="card full-width single-day-view">
    <h2>🥗 Today's Diet Plan: {dietPlanData.name}</h2>
    
    {(() => {
        // 1. Find the specific plan for today
        const todayPlan = dietPlanData.weeklyDietPlan.find(dayPlan => {
            const rowDateNormalized = dayPlan.date ? getFormattedDate(new Date(dayPlan.date)) : null;
            return rowDateNormalized === todayNormalized;
        }) || dietPlanData.weeklyDietPlan[0]; // Fallback to first entry if date match fails

        return isMobile ? (
            /* Mobile View (Kept simple) */
            <div className="daily-plan-card today-highlight">
                <h4>{todayPlan.date ? new Date(todayPlan.date).toLocaleDateString() : 'Today'}</h4>
                <ul className="meal-list">
                    {todayPlan.meals.map((meal, index) => (
                        <li key={index}>
                            <span className="meal-time-label">{mealTimings[index]}:</span> 
                            <span className="meal-name">{meal || `Meal ${index + 1}`}</span>
                        </li>
                    ))}
                </ul>
            </div>
        ) : (
            /* Desktop View (Filtered to only today) */
            <div className="focused-meal-container">
                <div className="date-header">
                    {todayPlan.date ? new Date(todayPlan.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'Today'}
                </div>
                <table className="meals-table single-row">
                    <thead>
                        <tr>
                            {mealTimings.map((timing, index) => (
                                <th key={index}>{timing}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {mealTimings.map((_, mealIndex) => (
                                <td key={mealIndex} className="meal-cell">
                                    {todayPlan.meals[mealIndex] || 'N/A'}
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    })()}

    <p className="uploaded-at">
        Last updated: {new Date(dietPlanData.uploadedAt.toDate()).toLocaleString()}
    </p>
</div>
    );
};

// 5. Meal Checklist (Remains the same)
const MealChecklist = ({ mealTimings, uid, onRefresh, selectedDate, today }) => {
    const [checkedMeals, setCheckedMeals] = useState(() => mealTimings.map(() => false));
    const [message, setMessage] = useState(''); // State for cheerful message

    // Effect to fetch adherence when the selectedDate or uid changes
    useEffect(() => {
        const fetchAdherence = async () => {
            if (!uid) return;
            setMessage(''); // Clear message on date change
            try {
                // Fetch data for the selectedDate
                const docRef = doc(db, 'progress', uid, 'dailyProgress', selectedDate);
                const docSnap = await getDoc(docRef);
                const mealCount = mealTimings.length;
                
                if (docSnap.exists() && docSnap.data().mealAdherence) {
                    const fetchedMeals = docSnap.data().mealAdherence;
                    // Ensure the array matches the current meal count, padding/truncating if necessary
                    const finalMeals = fetchedMeals.slice(0, mealCount).concat(Array(mealCount - fetchedMeals.length).fill(false));
                    setCheckedMeals(finalMeals);
                    
                    if (finalMeals.every(c => c)) {
                        setMessage('✅ Keep up the great work!');
                        setTimeout(() => setMessage(''), 5000);
                    }
                } else {
                    // Reset to all unchecked for the new date
                    setCheckedMeals(Array(mealCount).fill(false));
                }
            } catch (error) {
                console.error("Error fetching adherence:", error);
            }
        };
        fetchAdherence();
    }, [uid, selectedDate, mealTimings]); // Re-run when date or mealTimings change

    const handleCheck = async (index) => {
        const newCheckedMeals = checkedMeals.map((val, i) => i === index ? !val : val);
        setCheckedMeals(newCheckedMeals);

        if (!uid) return;

        try {
            // ⚠️ FIREBASE WRITE OPERATION: Use selectedDate
            const docRef = doc(db, 'progress', uid, 'dailyProgress', selectedDate);
            await setDoc(docRef, {
                mealAdherence: newCheckedMeals,
                timestamp: new Date().toISOString()
            }, { merge: true });

            // ⭐ Add Cheerful Message Logic
            if (newCheckedMeals.every(c => c)) {
                setMessage('🎉 Fantastic job! All meals tracked for the selected day! 💪');
                setTimeout(() => setMessage(''), 5000);
            } else {
                setMessage(''); // Clear message if user unchecks something later
            }

            // ⭐ Call the refresh function passed from the parent component, but only if logging for a day within the weekly report range
            if (onRefresh && getLastSevenDays().includes(selectedDate)) onRefresh();

        } catch (error) {
            console.error("Error saving adherence:", error);
            setMessage('❌ Failed to save adherence.');
            setTimeout(() => setMessage(''), 5000);
        }
    };

    const dateTitle = selectedDate === today ? 'Today' : new Date(selectedDate).toLocaleDateString();

    return (
        // Meal Checklist component naturally uses the 'checklist-card' class from the parent JSX
        <div className="card checklist-card">
            <h3>✅ Checklist for {dateTitle}</h3>
            {/* ⭐ Display Cheerful Message */}
            {message && <div className={`submission-message ${message.startsWith('❌') ? 'error' : 'success'}`}>{message}</div>}
            <div className="checklist-items">
                {mealTimings.map((timing, index) => (
                    <div key={index} className="checklist-item" onClick={() => handleCheck(index)}>
                        <input type="checkbox" checked={checkedMeals[index]} readOnly />
                        <label className={checkedMeals[index] ? 'checked' : ''}>
                            {timing || `Meal ${index + 1}`}
                        </label>
                    </div>
                ))}
                <div className="checklist-summary">
                    {checkedMeals.filter(c => c).length} out of {mealTimings.length} meals completed!
                </div>
                
            </div>
        </div>
    );
};


// 6. Message Dietitian / Feedback (UPDATED to wrap AppNotifications with the correct card class)
const MessageFeedback = () => {
    // 🎯 FIX 1: Wrap AppNotifications with the required card and grid-span class
    return (
        <div className="card notifications-card">
            <AppNotifications />
        </div>
    );
};

// 7. Challenge of the Day (UPDATED to wrap FocusFlowGame with the correct card class)
const ChallengeOfTheDay = () => {
    // 🎯 FIX 2: Wrap FocusFlowGame with the required card and grid-span class
    return (
            <FocusFlowGame />
        
    );
};

// --- Main Dashboard Component (UPDATED FETCH LOGIC & STATE) ---
const CustomerDashboard = () => {
    const today = getFormattedDate(new Date());
    
    // ⭐ NEW STATE: The date the user has selected to view/log stats for
    const [selectedDate, setSelectedDate] = useState(today);

    // ... (All other state declarations remain the same) ...
    const [dietPlanData, setDietPlanData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentUser, setCurrentUser] = useState(null);
    const [profileName, setProfileName] = useState(null);
    const [dailyStats, setDailyStats] = useState(DEFAULT_DAILY_STATS); 
    const [weeklyProgressData, setWeeklyProgressData] = useState([]);
    
    // ⭐ ASYNC FUNCTION: Fetch stats for the SELECTED DATE (FIXED TO ENSURE NUMBERS)
    const fetchStatsForDate = useCallback(async (uid, date) => {
        const statsDocRef = doc(db, 'progress', uid, 'dailyProgress', date);
        const statsDocSnap = await getDoc(statsDocRef);

        let fetchedStats = {};
        if (statsDocSnap.exists()) {
            fetchedStats = statsDocSnap.data();
        }
        
        // ⭐ CRITICAL FIX: Ensure all required stats are numbers, defaulting to 0 if missing/null/undefined.
        return {
            exerciseTime: Number(fetchedStats.exerciseTime) || 0,
            currentWeight: Number(fetchedStats.currentWeight) || 0,
            sleepTime: Number(fetchedStats.sleepTime) || 0,
            waterIntake: Number(fetchedStats.waterIntake) || 0,
            
            // Keep goals/initialWeight from fetched data if present, otherwise rely on calling component to inject defaults
            sleepGoal: Number(fetchedStats.sleepGoal) || DEFAULT_DAILY_STATS.sleepGoal,
            waterGoal: Number(fetchedStats.waterGoal) || DEFAULT_DAILY_STATS.waterGoal,
            initialWeight: Number(fetchedStats.initialWeight) || DEFAULT_DAILY_STATS.initialWeight,
        };
        
    }, []); 
    
    // ⭐ ASYNC FUNCTION: Fetch the user's latest weight/goals from a 'latest' or 'profile' document
    const fetchLatestMetrics = useCallback(async (uid) => {
        const statsDocRef = doc(db, 'progress', uid); // Using the general progress document for latest metrics
        const statsDocSnap = await getDoc(statsDocRef);
        return statsDocSnap.exists() ? statsDocSnap.data() : {};
    }, []);
    
    // ⭐ ASYNC FUNCTION: Fetch weekly daily progress data (Remains the same)
    const fetchWeeklyProgress = useCallback(async (uid) => {
        const lastSevenDays = getLastSevenDays().sort((a, b) => (a > b ? 1 : -1));
        const weeklyData = [];

        try {
            for (const date of lastSevenDays) {
                const docRef = doc(db, 'progress', uid, 'dailyProgress', date);
                const docSnap = await getDoc(docRef);

                weeklyData.push({
                    date: date,
                    mealAdherence: docSnap.exists() ? docSnap.data().mealAdherence : null,
                });
            }
            setWeeklyProgressData(weeklyData);
        } catch (err) {
            console.error('Error fetching weekly progress data:', err);
        }
    }, []);
    
    // Effect 1: Handle window resize (Remains the same)
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    // Effect 2: Consolidated Auth State and Initial Data Fetch
    useEffect(() => {
        let isMounted = true;

        const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
            if (!isMounted) return;

            setCurrentUser(user);
            setLoading(true);
            setError(null);

            if (user) {
                const uid = user.uid;

                try {
                    // 1. FETCH PROFILE DATA
                    const profileDocRef = doc(db, 'profiles', uid);
                    const profileDocSnap = await getDoc(profileDocRef);
                    let profileData = {};
                    if (profileDocSnap.exists()) {
                        profileData = profileDocSnap.data();
                        if (isMounted) {
                            setProfileName(profileData.name || null);
                        }
                    }
                    
                    // 2. FETCH LATEST GENERAL STATS (for goals/initial weight)
                    const latestMetrics = await fetchLatestMetrics(uid);

                    // 3. FETCH LATEST DIET PLAN (unchanged logic)
                    const dietPlansCollectionRef = collection(db, 'users', uid, 'dietPlans');
                    const q = query(dietPlansCollectionRef, orderBy('uploadedAt', 'desc'), limit(1));
                    const querySnapshot = await getDocs(q);

                    let planData = null;
                    if (!querySnapshot.empty) {
                        planData = querySnapshot.docs[0].data();
                    } else {
                         // --- FALLBACK/SIMULATED DIET PLAN DATA ---
                         const simulatedPlan = {
                            name: 'Keto Kickstart Plan',
                            startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                            mealTimings: ['Wake-up Drink', 'Breakfast', 'Lunch', 'Snack', 'Dinner'],
                            weeklyDietPlan: [
                                { date: new Date().toISOString(), meals: ['Water + Lemon', 'Scrambled Eggs (2)', 'Grilled Chicken Salad', 'Cheese Slice', 'Steak w/ Broccoli'] },
                                { date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), meals: ['Water + Apple Cider', 'Keto Smoothie', 'Tuna Salad', 'Nuts', 'Salmon w/ Asparagus'] },
                                { date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), meals: ['Water + Ginger', 'Omelette', 'Chicken Wings', 'Avocado', 'Pork Chops w/ Cauliflower'] },
                            ],
                            uploadedAt: { toDate: () => new Date() },
                            notes: 'Focus on healthy fats and stay hydrated!',
                         };
                         planData = simulatedPlan;
                    }
                    if (isMounted) setDietPlanData(planData);
                    
                    // 4. Set Initial dailyStats (Today's stats)
                    const todayStats = await fetchStatsForDate(uid, today);
                    const finalStats = {
                        ...DEFAULT_DAILY_STATS,
                        ...latestMetrics, 
                        ...todayStats, 
                        initialWeight: profileData.initialWeight || DEFAULT_DAILY_STATS.initialWeight
                    };
                    if (isMounted) setDailyStats(finalStats);
                    
                    // 5. FETCH WEEKLY PROGRESS DATA
                    await fetchWeeklyProgress(uid);

                } catch (err) {
                    console.error('Error fetching data:', err);
                    if (isMounted) {
                        setError('Failed to load dashboard data. Please check your network and permissions.');
                    }
                } finally {
                    if (isMounted) {
                        setLoading(false);
                    }
                }
            } else {
                // Not logged in logic
                if (isMounted) {
                    setDietPlanData(null);
                    setDailyStats(DEFAULT_DAILY_STATS);
                    setError('Please log in to view your dashboard.');
                    setLoading(false);
                }
            }
        });

        return () => {
            isMounted = false;
            unsubscribeAuth();
        };
    }, [today, fetchStatsForDate, fetchLatestMetrics, fetchWeeklyProgress]);

    // ⭐ Effect 3: Fetch stats whenever selectedDate changes
    useEffect(() => {
        if (currentUser && selectedDate) {
             fetchStatsForDate(currentUser.uid, selectedDate)
                 .then(fetchedStats => {
                     // Blend fetched stats with general goals/initial weight to keep QuickStatsDisplay correct
                     const baseStats = { 
                         sleepGoal: dailyStats.sleepGoal, 
                         waterGoal: dailyStats.waterGoal, 
                         initialWeight: dailyStats.initialWeight 
                     };
                     setDailyStats({...baseStats, ...fetchedStats});
                 })
                 .catch(err => console.error("Error fetching date specific stats:", err));
        }
    }, [currentUser, selectedDate, fetchStatsForDate, dailyStats.sleepGoal, dailyStats.waterGoal, dailyStats.initialWeight]); // Added dailyStats dependencies
    
    // Handler for date change
    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
    };


    if (loading) {
        return <div className="dashboard-container customer-dashboard">Loading dashboard...</div>;
    }

    if (error) {
        return <div className="dashboard-container customer-dashboard">Error: {error}</div>;
    }

    const mealTimings = dietPlanData?.mealTimings || Array(5).fill('').map((_, i) => `Meal ${i + 1}`);

    return (
        <div className="dashboard-container customer-dashboard">
            <div className="dashboard-header">
                <h1>My Dashboard</h1>
            </div>

            <MotivationalQuote currentUser={currentUser} profileName={profileName} />

            {/* ADDED DATE SELECTOR */}
            <div className="date-selector-container card full-width">
                <FaCalendarAlt className="icon" />
                <label htmlFor="date-picker">Logging Data For: </label>
                <input 
                    type="date" 
                    id="date-picker" 
                    value={selectedDate} 
                    onChange={handleDateChange} 
                    max={undefined} // Prevents logging for future dates
                />
            </div>

            <div className="dashboard-grid">

                {/* Row 1: Quick Stats DISPLAY (Shows the stats loaded for the selected date) */}
                <QuickStatsDisplay 
                    exerciseTime={dailyStats.exerciseTime}
                    currentWeight={dailyStats.currentWeight}
                    initialWeight={dailyStats.initialWeight}
                    sleepTime={dailyStats.sleepTime}
                    sleepGoal={dailyStats.sleepGoal}
                    waterIntake={dailyStats.waterIntake}
                    waterGoal={dailyStats.waterGoal}
                />

                {/* Row 2: Daily Stats Input Logger - uses selectedDate */}
                <DailyStatsLogger 
                    setStats={setDailyStats} 
                    currentStats={dailyStats} 
                    uid={currentUser?.uid}
                    selectedDate={selectedDate}
                    today={today}
                />

                {/* Row 3: Diet Plan Section (Full Width) */}
                {dietPlanData ? (
                    <DietPlanSection 
                        dietPlanData={dietPlanData} 
                        isMobile={isMobile} 
                        mealTimings={mealTimings} 
                    />
                ) : (
                    <div className="card full-width"><p>No diet plan available yet. Please contact your dietitian.</p></div>
                )}

                {/* 🌟 START ROW 4: Checklist (4 columns: 1/5) & Challenge (8 columns: 5/-1) 🌟 */}
                {/* 1. Checklist (Class is applied inside MealChecklist component) */}
                {dietPlanData && <MealChecklist 
                    mealTimings={mealTimings} 
                    uid={currentUser?.uid} 
                    onRefresh={() => fetchWeeklyProgress(currentUser?.uid)} 
                    selectedDate={selectedDate} 
                    today={today}
                />} 

                {/* 2. Challenge (Class is applied inside ChallengeOfTheDay component) */}
                <ChallengeOfTheDay /> 
                
                {/* 🌟 START ROW 5: Graph (8 columns: 1/9) & Notifications (4 columns: 9/-1) 🌟 */}
                {/* 3. Graph (Must be wrapped with card class for grid-column to work) */}
                {/* 🎯 FIX 3: Add explicit wrapper for RealProgressGraph */}
                
                    <RealProgressGraph /> 
                

                {/* 4. Notifications (Class is applied inside MessageFeedback component) */}
                <MessageFeedback />
                
                {/* Row 6: Summary Section (Full Width) */}
                <WeeklyReportSummary 
                    uid={currentUser?.uid} 
                    dietPlanData={dietPlanData} 
                    dailyStats={dailyStats} 
                    weeklyProgressData={weeklyProgressData} 
                />
            </div>
        </div>
    );
};

export default CustomerDashboard;